/** Shared helpers for connection row lookup (handles duplicate kind rows). */
import { isConfiguredStatus, isPairingSessionActive } from "./connectionStatus";

function rowScore(row) {
  if (!row) return -1;
  if (isConfiguredStatus(
    row.status, row.configured, row.account_identifier, row.channel_config, row.kind,
  )) return 3;
  if (row.status === "connected") return 2;
  if (row.status === "pairing" && isPairingSessionActive(row)) return 1;
  return 0;
}

/** Prefer configured/connected row when duplicates exist for the same kind. */
export function rowByKind(rows, kind) {
  const matches = (rows || []).filter((r) => r && r.kind === kind);
  if (matches.length === 0) return null;
  return matches.reduce((best, row) => (rowScore(row) > rowScore(best) ? row : best));
}

export function rowsByKindMap(rows) {
  const kinds = [...new Set((rows || []).map((r) => r.kind))];
  return Object.fromEntries(kinds.map((k) => [k, rowByKind(rows, k)]));
}

export function whatsappDisplayMeta(row) {
  const wa = row?.channel_config?.whatsapp || {};
  const rootCfg = row?.channel_config || {};
  return {
    phoneNumber: row?.account_identifier || wa.phone_number || null,
    displayName: wa.push_name || wa.account_name || null,
    connectedAt: rootCfg.connected_at || wa.paired_at || row?.updated_at || null,
    connectionStatus: row?.configured ? "connected" : (row?.status || "disconnected"),
  };
}

export function telegramDisplayMeta(row) {
  const tg = row?.channel_config?.telegram || {};
  return {
    botName: row?.bot_name || tg.bot_name || null,
    botUsername: row?.bot_username || tg.bot_username || row?.account_identifier || null,
    connectionStatus: tg.connection_status || (row?.configured ? "connected" : null),
    validatedAt: row?.validated_at || tg.validated_at || tg.configured_at || null,
    openclawSynced: row?.openclaw_synced ?? tg.openclaw_synced ?? null,
    openclawError: row?.openclaw_error ?? tg.openclaw_error ?? null,
    ingestMode: row?.ingest_mode ?? tg.ingest_mode ?? null,
    ingestReady: row?.ingest_ready ?? tg.ingest_ready ?? null,
  };
}

export function redditDisplayMeta(row) {
  const r = row?.channel_config?.reddit_oauth || {};
  const rawUsername = row?.reddit_username || r.reddit_username || row?.account_identifier;
  let username = null;
  if (rawUsername) {
    username = String(rawUsername).startsWith("u/") ? rawUsername : `u/${rawUsername}`;
  }
  return {
    username,
    validatedAt: row?.oauth_validated_at || r.oauth_validated_at || null,
    connectionStatus: row?.configured ? "connected" : (row?.status || "disconnected"),
  };
}

export function quoraDisplayMeta(row) {
  const q = row?.channel_config?.quora || {};
  return {
    displayName: row?.display_name || q.display_name || row?.account_identifier || null,
    profileUrl: row?.profile_url || q.profile_url || null,
    configuredAt: q.configured_at || null,
    openclawSynced: q.openclaw_synced ?? null,
  };
}
